/**
 * Desmond (DeSmuME WASM) Core Implementation
 * Wraps the Desmond web component to implement IEmulatorCore interface
 */

import { IEmulatorCore, EmulatorState, EmulatorCoreMetadata, DSButton } from '../IEmulatorCore';

// Declare Desmond web component type
interface DesmondPlayer extends HTMLElement {
  loadURL(url: string, callback?: () => void): void;
  enableMicrophone(): void;
}

// Declare global for web component
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'desmond-player': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement> & {
        id?: string;
        style?: React.CSSProperties;
      }, HTMLElement>;
    }
  }
}

export class DesmondCore implements IEmulatorCore {
  private player: DesmondPlayer | null = null;
  private state: EmulatorState = {
    isRunning: false,
    isPaused: false,
    fps: 60, // Desmond doesn't expose FPS, assume 60
    romLoaded: false,
  };
  private isInitialized = false;

  getMetadata(): EmulatorCoreMetadata {
    return {
      name: 'Desmond',
      version: '1.0.0',
      description: 'DeSmuME WASM port - stable and lightweight',
      features: {
        cloudSaves: true,       // Via DesmondSaveBridge (localforage → cloud)
        customControls: true,   // Via DesmondKeyboardRemapper
        saveStates: false,      // Not implemented
        fpsDisplay: false,      // No FPS API
        touchScreen: true,      // Supports touch
      },
      limitations: [
        'No save state support',
      ],
    };
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    console.log('🎮 Initializing Desmond core...');

    // Load Desmond script
    await this.loadDesmondScript();

    // Note: Desmond doesn't use a proper custom element definition
    // It just looks for <desmond-player> elements and initializes them
    // So we don't wait for customElements.whenDefined()

    console.log('✅ Desmond script loaded');

    // Give the script a moment to initialize
    await new Promise(resolve => setTimeout(resolve, 100));

    this.isInitialized = true;
    console.log('✅ Desmond core initialized');
  }

  private async loadDesmondScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (document.querySelector('script[src*="desmond"]')) {
        console.log('✓ Desmond script already loaded');
        resolve();
        return;
      }

      console.log('📥 Loading Desmond script...');

      // Set timeout for script loading
      const timeout = setTimeout(() => {
        reject(new Error('Timeout: Desmond script took too long to load (>15s)'));
      }, 15000);

      const script = document.createElement('script');
      script.src = '/wasm/desmond.js';
      script.async = true;
      script.onload = () => {
        clearTimeout(timeout);
        console.log('✓ Desmond script loaded');
        resolve();
      };
      script.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('Failed to load Desmond script from /wasm/desmond.js'));
      };
      document.head.appendChild(script);
    });
  }

  /**
   * Create the Desmond player element
   * This must be called externally and the element added to the DOM
   * Note: The desmond.js script will automatically initialize this element
   * and add the loadURL method to it once it's in the DOM
   */
  createPlayerElement(): DesmondPlayer {
    const player = document.createElement('desmond-player') as DesmondPlayer;
    player.id = 'desmond-player-instance';
    player.style.width = '100%';
    player.style.height = 'auto';
    this.player = player;

    console.log('✅ Created <desmond-player> element (will be initialized by script once added to DOM)');
    return player;
  }

  /**
   * Set the player element reference (if created externally)
   */
  setPlayerElement(player: DesmondPlayer): void {
    this.player = player;
  }

  async loadROM(romBuffer: ArrayBuffer): Promise<void> {
    if (!this.player) {
      throw new Error('Desmond player element not created. Call createPlayerElement() first.');
    }

    // Wait for the desmond.js script to initialize the element
    // The script adds the loadURL method when it finds the element in the DOM
    console.log('⏳ Waiting for desmond.js to initialize the player element...');

    let attempts = 0;
    const maxAttempts = 50; // 5 seconds total (50 * 100ms)

    while (typeof (this.player as any).loadURL !== 'function' && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }

    // Check if loadURL method exists
    if (typeof (this.player as any).loadURL !== 'function') {
      throw new Error(
        'Desmond player element was not initialized by desmond.js script. ' +
        'The element may not be in the DOM, or the script failed to initialize it. ' +
        'Try refreshing the page.'
      );
    }

    console.log(`✅ Player initialized after ${attempts * 100}ms`);
    console.log(`📦 Loading ROM into Desmond (${romBuffer.byteLength} bytes)...`);

    // Create blob URL for ROM
    const blob = new Blob([romBuffer], { type: 'application/octet-stream' });
    const objectURL = URL.createObjectURL(blob);

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        URL.revokeObjectURL(objectURL);
        reject(new Error('ROM loading timed out after 30 seconds'));
      }, 30000);

      this.player!.loadURL(objectURL, () => {
        clearTimeout(timeout);
        URL.revokeObjectURL(objectURL);
        this.state.romLoaded = true;
        this.state.isRunning = true;
        console.log('✅ ROM loaded into Desmond');
        resolve();
      });
    });
  }

  getROMTitle(): string | null {
    // Desmond doesn't expose ROM title API
    return null;
  }

  reset(): void {
    console.warn('Desmond does not support programmatic reset. User must use in-emulator controls.');
  }

  runFrame(): void {
    // Desmond runs automatically, no manual frame control
  }

  pause(): void {
    this.state.isPaused = true;
    this.state.isRunning = false;
    console.warn('Desmond does not support programmatic pause. User must use in-emulator controls.');
  }

  resume(): void {
    this.state.isPaused = false;
    this.state.isRunning = true;
    console.warn('Desmond does not support programmatic resume. User must use in-emulator controls.');
  }

  getTopScreen(): Uint8Array | null {
    // Desmond renders internally, no access to screen buffers
    return null;
  }

  getBottomScreen(): Uint8Array | null {
    // Desmond renders internally, no access to screen buffers
    return null;
  }

  getAudioBuffer(): Float32Array | null {
    // Desmond handles audio internally
    return null;
  }

  setButtonState(_button: DSButton, _pressed: boolean): void {
    console.warn('Desmond does not support programmatic button input. Use keyboard controls.');
  }

  setTouchPosition(_x: number, _y: number): void {
    console.warn('Desmond touch input is handled by the web component itself.');
  }

  releaseTouchPosition(): void {
    console.warn('Desmond touch input is handled by the web component itself.');
  }

  getSaveRAM(): Uint8Array | null {
    console.warn('Desmond does not expose getSaveRAM API. Saves are stored in browser IndexedDB.');
    return null;
  }

  setSaveRAM(_saveData: Uint8Array): void {
    console.warn('Desmond does not expose setSaveRAM API. Cannot load external save data.');
  }

  isReady(): boolean {
    return this.isInitialized && this.state.romLoaded;
  }

  getState(): EmulatorState {
    return { ...this.state };
  }

  setFPS(fps: number): void {
    this.state.fps = fps;
  }

  destroy(): void {
    this.state.isRunning = false;
    this.state.isPaused = false;
    this.state.romLoaded = false;

    // Remove player element from DOM
    if (this.player && this.player.parentElement) {
      this.player.parentElement.removeChild(this.player);
    }

    this.player = null;
    // ROM buffer cleanup not needed - handled by Desmond

    console.log('✓ Desmond core destroyed');
  }
}
